import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

/**
 * AutoSEO Imports panel — every AutoSEO delivery: received, drafted,
 * awaiting approval, published, failed, skipped or duplicate. Read-only.
 * Lives inside the Editorial Dashboard (AutoSEO Imports tab).
 */

const FILTERS = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Draft" },
  { key: "awaiting_approval", label: "Awaiting Approval" },
  { key: "failed", label: "Failed" },
  { key: "skipped", label: "Skipped" },
  { key: "duplicate", label: "Duplicate" },
];

const BUCKET_STYLE = {
  published: "border-emerald-400/50 text-emerald-300",
  draft: "border-sky-400/50 text-sky-300",
  awaiting_approval: "border-gold/50 text-gold",
  failed: "border-red-400/60 text-red-300",
  skipped: "border-amber-400/50 text-amber-300",
  duplicate: "border-purple-400/50 text-purple-300",
};

const bucketLabel = (b) => (FILTERS.find((f) => f.key === b)?.label || b || "—");

export default function AutoSEOImportPanel({ initialFilter = "all" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [query, setQuery] = useState("");
  const [dryRun, setDryRun] = useState(null);
  const [numWorking, setNumWorking] = useState(false);
  const [numberingMode, setNumberingMode] = useState(null);
  const [modeBusy, setModeBusy] = useState(false);

  useEffect(() => {
    adminApi.numberingMode().then((d) => setNumberingMode(d.mode)).catch(() => {});
  }, []);

  const toggleNumberingMode = async () => {
    const next = numberingMode === "on_publish" ? "on_create" : "on_publish";
    const warn = next === "on_publish"
      ? "Start numbering TRIAL: new drafts get NO ART number; the next number is assigned only when you click Publish. Existing articles and numbers stay untouched. Switch back anytime."
      : "Switch back to the current behaviour: every new article gets an ART number at creation. Existing articles and numbers stay untouched.";
    if (!window.confirm(warn)) return;
    setModeBusy(true); setErr("");
    try {
      const res = await adminApi.setNumberingMode(next);
      setNumberingMode(res.mode);
    } catch (e) { setErr(e.message); }
    finally { setModeBusy(false); }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try { setData(await adminApi.autoseoImportStatus()); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setFilter(initialFilter); }, [initialFilter]);

  const runDryRun = async () => {
    setNumWorking(true); setErr("");
    try { setDryRun(await adminApi.refsBackfillDryRun()); }
    catch (e) { setErr(e.message); }
    finally { setNumWorking(false); }
  };

  const executeBackfill = async () => {
    if (!window.confirm("Assign the proposed ART numbers now? A database snapshot is taken first. Numbers are permanent.")) return;
    setNumWorking(true); setErr("");
    try {
      const res = await adminApi.refsBackfillExecute();
      setDryRun({ executed: true, ...res });
      await load();
    } catch (e) { setErr(e.message); }
    finally { setNumWorking(false); }
  };

  const rows = useMemo(() => {
    let all = data?.rows || [];
    if (filter !== "all") all = all.filter((r) => r.bucket === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      all = all.filter((r) =>
        (r.article_reference || "").toLowerCase().includes(q) ||
        (r.title || "").toLowerCase().includes(q) ||
        (r.slug || "").toLowerCase().includes(q) ||
        (r.autoseo_id || "").toLowerCase().includes(q));
    }
    return all;
  }, [data, filter, query]);

  const counts = data?.counts || {};

  return (
    <div data-testid="autoseo-import-panel">
      <p className="text-sm text-bone/60 mb-6 max-w-3xl">
        Every row is one webhook delivery (or a legacy import), joined with the article it
        produced. Nothing on this page publishes, deletes or resends anything.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="import-status-summary">
        <span className={`text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border ${data?.paused ? "border-amber-400/50 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`} data-testid="import-status-paused">
          {data?.paused ? "Imports paused" : "Imports active"}
        </span>
        <span className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-white/15 text-bone/60" data-testid="import-status-weekly">
          Week budget · {data?.weekly_created ?? 0} / {data?.weekly_limit ?? 7}
        </span>
        <span className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-white/15 text-bone/60">
          {data?.published_articles ?? 0} published · {data?.draft_articles ?? 0} drafts · {data?.total_articles ?? 0} AutoSEO articles
        </span>
        <button onClick={load} data-testid="import-status-refresh"
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10 ml-auto">
          <RefreshCw size={12} strokeWidth={1.5} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="import-status-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
              filter === f.key ? "border-gold text-gold bg-gold/10" : "border-white/15 text-bone/60 hover:border-gold/40"
            }`}>
            {f.label}{f.key !== "all" ? ` (${counts[f.key] || 0})` : ` (${data?.rows?.length || 0})`}
          </button>
        ))}
      </div>

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by ART-0011, title, slug or AutoSEO ID…"
        data-testid="import-status-search"
        className="w-full max-w-md mb-6 bg-white/[0.03] border border-white/15 focus:border-gold/60 outline-none px-3 py-2.5 text-sm text-bone placeholder:text-bone/30"
      />

      <div className="border border-gold/15 bg-white/[0.02] overflow-x-auto" data-testid="import-status-table">
        <table className="w-full text-left min-w-[1200px]">
          <thead>
            <tr className="border-b border-white/10">
              {["Date / time", "Ref", "Title", "Search term", "AutoSEO ID", "Result", "Reason / error", "Article", "EN", "NL", "Published", "Returned URL"].map((h) => (
                <th key={h} className="px-3 py-3 text-[10px] tracking-[0.22em] uppercase text-bone/50 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={12} className="px-4 py-6 text-sm text-bone/50">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-6 text-sm text-bone/50" data-testid="import-status-empty">No deliveries in this category.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.at}-${i}`} className="border-b border-white/5 last:border-b-0 align-top" data-testid={`import-row-${i}`}>
                <td className="px-3 py-2.5 text-[12px] font-mono text-bone/60 whitespace-nowrap">{String(r.at).slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2.5 text-[12px] font-mono text-gold whitespace-nowrap">{r.article_reference || "—"}</td>
                <td className="px-3 py-2.5 text-[13px] text-bone max-w-[260px] truncate" title={r.title}>
                  {r.title || "—"}
                  {String(r.action || "").startsWith("skipped") && (
                    <p className="mt-0.5 text-[10px] tracking-[0.14em] uppercase text-amber-300" data-testid={`import-skip-reason-${i}`}>
                      {"SKIPPED — " + (r.action === "skipped_weekly_limit" ? "WEEKLY LIMIT"
                        : r.action === "skipped_batch_guard" ? "BATCH GUARD"
                        : r.action === "skipped_duplicate" ? "DUPLICATE"
                        : r.action === "skipped_paused" ? "PAUSED"
                        : r.action === "skipped_tombstone" ? "TOMBSTONE"
                        : r.action === "skipped_topic_collision" ? "TOPIC COLLISION"
                        : r.action.replace("skipped_", "").replace(/_/g, " ").toUpperCase())}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-bone/60 max-w-[140px] truncate" title={r.search_term}>{r.search_term || "—"}</td>
                <td className="px-3 py-2.5 text-[11px] font-mono text-bone/50 max-w-[110px] truncate" title={r.autoseo_id}>{r.autoseo_id || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 border ${BUCKET_STYLE[r.bucket] || "border-white/20 text-bone/50"}`}>
                    {bucketLabel(r.bucket)}
                  </span>
                  <p className="mt-1 text-[10px] font-mono text-bone/35">{r.action}</p>
                </td>
                <td className="px-3 py-2.5 text-[12px] text-bone/60 max-w-[180px] truncate" title={r.reason || r.error}>{r.error || r.reason || "—"}</td>
                <td className="px-3 py-2.5 text-[12px] whitespace-nowrap">
                  {r.slug ? (
                    <Link to={`/admin/journal?edit=${encodeURIComponent(r.slug)}`} className="text-gold hover:text-bone">{r.slug.slice(0, 28)}</Link>
                  ) : "—"}
                </td>
                <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide whitespace-nowrap">
                  <span className={r.en_status === "approved" ? "text-emerald-300" : r.en_status === "awaiting approval" ? "text-gold" : "text-bone/35"}>{r.en_status || "—"}</span>
                </td>
                <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide whitespace-nowrap">
                  <span className={r.nl_status === "approved" ? "text-emerald-300" : r.nl_status === "awaiting approval" ? "text-gold" : "text-bone/35"}>{r.nl_status || "—"}</span>
                </td>
                <td className="px-3 py-2.5 text-[12px] whitespace-nowrap">
                  {r.published ? (
                    <a href={r.live_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200">
                      Live <ExternalLink size={11} strokeWidth={1.5} />
                    </a>
                  ) : (
                    <span className="text-bone/35">No</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[11px] font-mono text-bone/45 max-w-[200px] truncate" title={r.returned_url}>{r.returned_url || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[12px] text-bone/40 max-w-3xl">
        Legend: <span className="text-sky-300">Draft</span> = imported, translation still incomplete ·{" "}
        <span className="text-gold">Awaiting Approval</span> = both languages present, waiting for EN + NL founder approval ·{" "}
        <span className="text-purple-300">Duplicate</span> = redelivery of an existing article (updated in place, never copied) ·{" "}
        <span className="text-amber-300">Skipped</span> = paused / weekly limit / deleted-by-founder ·{" "}
        <span className="text-red-300">Failed</span> = technical import error.
      </p>

      {err && <p className="mt-4 text-sm text-red-400" data-testid="import-status-error">{err}</p>}

      {/* Article numbering — founder-triggered backfill with mandatory dry run */}
      <div className="mt-10 border border-gold/20 bg-white/[0.02] p-6" data-testid="numbering-panel">
        <h2 className="font-serif text-xl text-bone mb-2">Article numbering (ART-0001…)</h2>

        {/* TRIAL: numbering mode feature flag */}
        <div className={`mb-5 p-4 border ${numberingMode === "on_publish" ? "border-amber-400/40 bg-amber-400/[0.04]" : "border-white/10 bg-white/[0.01]"}`} data-testid="numbering-mode-panel">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border ${numberingMode === "on_publish" ? "border-amber-400/50 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`} data-testid="numbering-mode-badge">
              {numberingMode === "on_publish" ? "TRIAL · number at publish" : "Default · number at creation"}
            </span>
            <button onClick={toggleNumberingMode} disabled={modeBusy || numberingMode === null} data-testid="numbering-mode-toggle"
              className="ml-auto px-4 py-2 text-[10px] tracking-[0.2em] uppercase border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-40">
              {numberingMode === "on_publish" ? "Switch back to default" : "Start trial mode"}
            </button>
          </div>
          <p className="mt-2 text-[12px] text-bone/50 max-w-3xl">
            {numberingMode === "on_publish"
              ? "Trial active: new drafts keep only their internal ID. The next ART number (after the last one assigned) is given automatically the moment you click Publish. Deleted drafts never create gaps. Published articles are never renumbered. Switching back changes future behaviour only."
              : "Default: every new article (draft included) receives its ART number at creation. Start the trial to test publish-time numbering without touching any existing data."}
          </p>
        </div>
        <p className="text-[13px] text-bone/50 max-w-3xl mb-4">
          Assigns a permanent, unique reference to every article that does not have one yet.
          Order: oldest published first, then drafts by creation date. Always run the dry run
          first and review the mapping — execution takes a database snapshot and is irreversible.
          Numbers are never reused or changed.
        </p>
        <div className="flex flex-wrap gap-3 mb-4">
          <button onClick={runDryRun} disabled={numWorking} data-testid="refs-dryrun-button"
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-bone/30 text-bone/80 hover:border-gold hover:text-gold disabled:opacity-40">
            Preview mapping (dry run)
          </button>
          {dryRun && !dryRun.executed && (dryRun.mapping || []).length > 0 && (
            <button onClick={executeBackfill} disabled={numWorking} data-testid="refs-execute-button"
              className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase bg-gold text-ink hover:bg-gold-hover disabled:opacity-40">
              Assign these numbers now
            </button>
          )}
        </div>
        {dryRun && !dryRun.executed && (
          <div data-testid="refs-dryrun-result">
            <p className="text-[12px] text-bone/60 mb-2">
              Already numbered: {dryRun.already_numbered} · To assign: {dryRun.to_assign}
              {dryRun.to_assign === 0 && " — nothing to do."}
            </p>
            <ul className="space-y-1 max-h-72 overflow-y-auto">
              {(dryRun.mapping || []).map((m) => (
                <li key={m.reference} className="text-[12px] font-mono text-bone/70">
                  <span className="text-gold">{m.reference}</span> → {m.title}
                  <span className="text-bone/35"> ({m.status}{m.published_at ? `, published ${String(m.published_at).slice(0, 10)}` : ""})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {dryRun?.executed && (
          <p className="text-[13px] text-emerald-300" data-testid="refs-executed-result">
            Assigned {dryRun.count} reference{dryRun.count === 1 ? "" : "s"}.
            {dryRun.backup_collection ? ` Snapshot: ${dryRun.backup_collection}.` : ""}
          </p>
        )}
      </div>
    </div>
  );
}
